import React, { useEffect, useState, useRef } from 'react';
import { 
  Phone, Mail, MessageSquare, Clock, Calendar,
  CheckCircle, ArrowRight, Star, Send, ChevronDown
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';


const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    projectType: '',
    projectLocation: '',
    projectValue: '',
    timeline: '',
    description: '',
    preferredContact: 'phone',
    bestTimeToCall: ''
  });
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error
  const [formErrors, setFormErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const projectTypeOptions = [
    { value: '', label: 'Select project type' },
    { value: 'wood-framing', label: 'Wood Framing' },
    { value: 'steel-framing', label: 'Steel Structural' },
    { value: 'mixed-framing', label: 'Wood & Steel' },
    { value: 'demolition', label: 'Demolition' },
    { value: 'siding', label: 'Siding' },
    { value: 'consultation', label: 'Consultation Only' }
  ];

  useEffect(() => {
    document.title = "Contact Ceballos Total Framing - Arizona's Premier Structural Specialists | Get Your Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Ceballos Total Framing for your luxury framing project. Serving Paradise Valley, Scottsdale, and Arcadia. CR-7 Licensed. Call 602-448-1723 for consultation.');
    }
  }, []);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDropdownSelect = (value, label) => {
    setFormData(prev => ({ ...prev, projectType: value }));
    setIsDropdownOpen(false);
    // Clear error when user selects
    if (formErrors.projectType) {
      setFormErrors(prev => ({ ...prev, projectType: '' }));
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.projectType) errors.projectType = 'Project type is required';
    if (!formData.description.trim()) errors.description = 'Project description is required';
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setFormStatus('submitting');
    
    try {
      const response = await fetch('https://formspree.io/f/movneylo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          projectType: formData.projectType,
          projectLocation: formData.projectLocation,
          projectValue: formData.projectValue,
          timeline: formData.timeline,
          description: formData.description,
          preferredContact: formData.preferredContact,
          bestTimeToCall: formData.bestTimeToCall,
          _subject: `New Contact Form Submission - ${formData.firstName} ${formData.lastName}`,
        }),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          projectType: '',
          projectLocation: '',
          projectValue: '',
          timeline: '',
          description: '',
          preferredContact: 'phone',
          bestTimeToCall: ''
        });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-72">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/ArolloVerde_CamelbackMountain_Ramada.jpg" 
            alt="Contact Hero Background - Arollo Verde Camelback Mountain Ramada"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.5) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gray-900/40"></div>
        </div>

        <div className="relative z-20 text-center text-white px-6 max-w-8xl mx-auto">
          <AnimatedSection delay={200}>
            <div className="text-center">
              <div className="inline-block mb-8">
                
              </div>
              <h1 className="text-5xl md:text-7xl font-light mb-8 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Let's Build Your <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Vision</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Connect with Arizona's most trusted structural specialists
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Reach Out <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Today</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-900 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 text-center h-full" style={{ borderRadius: '12px' }}>
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '12px' }}>
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Call Directly</h3>
                <p className="text-gray-300 mb-6 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Speak directly with Aureliano for immediate consultation</p>
                <a 
                  href="tel:602-448-1723"
                  className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-3 transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-amber-500/25 text-lg font-medium"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  602.448.1723
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="bg-gray-900 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 text-center h-full" style={{ borderRadius: '12px' }}>
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '12px' }}>
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Email Us</h3>
                <p className="text-gray-300 mb-6 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Send detailed project information for comprehensive response</p>
                <a 
                  href="mailto:ceballos.4@outlook.com"
                  className="inline-block border border-amber-400/60 text-white px-8 py-3 hover:bg-amber-400/10 transition-all duration-500 shadow-xl text-lg font-medium"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  ceballos.4@outlook.com
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="bg-gray-900 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 text-center h-full" style={{ borderRadius: '12px' }}>
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '12px' }}>
                  <MessageSquare className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Project Form</h3>
                <p className="text-gray-300 mb-6 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Complete our detailed form for personalized consultation</p>
                <button 
                  onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  className="inline-block border border-amber-400/60 text-white px-8 py-3 hover:bg-amber-400/10 transition-all duration-500 shadow-xl text-lg font-medium"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  Get Started
                </button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Business Hours Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-800 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Business Hours</h3>
                </div>
                
                <div className="space-y-4 text-gray-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="font-light">Monday - Friday</span>
                    <span className="text-amber-400 font-medium">7:00 AM - 3:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="font-light">Saturday & Sunday</span>
                    <span className="text-amber-400 font-medium">Emergency Only</span>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-amber-400/5 border border-amber-400/20" style={{ borderRadius: '8px' }}>
                  <h4 className="text-lg font-medium text-amber-400 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Best Times to Call</h4>
                  <p className="text-gray-300 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Weekday mornings or late afternoons for immediate response. Emergency calls welcome anytime.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="bg-gray-800 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Consultation Process</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-amber-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Initial Contact</h4>
                      <p className="text-gray-300 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Phone consultation to understand your project scope and requirements</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-amber-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Site Assessment</h4>
                      <p className="text-gray-300 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>On-site evaluation and detailed project analysis</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-amber-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Detailed Proposal</h4>
                      <p className="text-gray-300 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Comprehensive estimate with materials, timeline, and approach</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Tell Us About Your <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Project</span>
              </h2>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <div className="bg-gray-900 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
              
              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-amber-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Thank You!</h3>
                  <p className="text-gray-300 mb-6 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Your project inquiry has been received. We'll contact you within 24 hours to discuss your vision.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="text-amber-400 hover:text-amber-300 transition-colors duration-300 font-medium"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.firstName ? 'border-red-500' : 'border-gray-600'} text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300`}
                        style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                        placeholder="Your first name"
                      />
                      {formErrors.firstName && (
                        <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.lastName ? 'border-red-500' : 'border-gray-600'} text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300`}
                        style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                        placeholder="Your last name"
                      />
                      {formErrors.lastName && (
                        <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Contact Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.email ? 'border-red-500' : 'border-gray-600'} text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300`}
                        style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                        placeholder="your.email@example.com"
                      />
                      {formErrors.email && (
                        <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.phone ? 'border-red-500' : 'border-gray-600'} text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300`}
                        style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                        placeholder="(602) 123-4567"
                      />
                      {formErrors.phone && (
                        <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Project Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Project Type *
                      </label>
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={toggleDropdown}
                          className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.projectType ? 'border-red-500' : 'border-gray-600'} text-white focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/30 hover:border-gray-500 transition-all duration-300 rounded-lg text-left flex items-center justify-between`}
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <span className={formData.projectType ? 'text-white' : 'text-gray-400'}>
                            {formData.projectType ? projectTypeOptions.find(opt => opt.value === formData.projectType)?.label : 'Select project type'}
                          </span>
                          <ChevronDown 
                            className={`h-5 w-5 text-amber-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                          />
                        </button>
                        
                        {isDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-auto">
                            {projectTypeOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleDropdownSelect(option.value, option.label)}
                                className={`w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${
                                  option.value === '' ? 'text-gray-400' : 'text-white'
                                } ${formData.projectType === option.value ? 'bg-gray-700 text-amber-400' : ''}`}
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {formErrors.projectType && (
                        <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.projectType}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Project Location
                      </label>
                      <input
                        type="text"
                        name="projectLocation"
                        value={formData.projectLocation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300"
                        style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                        placeholder="Paradise Valley, Arcadia, etc."
                      />
                    </div>
                  </div>

                  {/* Project Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Project Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={6}
                      className={`w-full px-4 py-3 bg-gray-800 border ${formErrors.description ? 'border-red-500' : 'border-gray-600'} text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none transition-colors duration-300 resize-vertical`}
                      style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                      placeholder="Please describe your project scope, timeline, and any specific requirements..."
                    />
                    {formErrors.description && (
                      <p className="mt-1 text-sm text-red-400" style={{ fontFamily: 'Inter, sans-serif' }}>{formErrors.description}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="inline-flex items-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-amber-500/25 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Send Project Details
                          <Send className="ml-3 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                What Clients <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Say</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-800 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-6">
                  <div className="flex space-x-1 mr-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>McIntyre Development</span>
                </div>
                <blockquote className="text-gray-300 text-lg font-light leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  "Professional, reliable, and delivers exactly what's promised. Ceballos Total Framing is our go-to for all structural work. Zero issues, every time."
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>DM</span>
                  </div>
                  <div>
                    <div className="text-white font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Dan McIntyre</div>
                    <div className="text-gray-400 text-sm font-light" style={{ fontFamily: 'Inter, sans-serif' }}>General Contractor</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="bg-gray-800 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-6">
                  <div className="flex space-x-1 mr-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Malapai Builders</span>
                </div>
                <blockquote className="text-gray-300 text-lg font-light leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  "Ceballos Total Framing consistently delivers superior quality and reliability. Their expertise in both wood and steel framing makes them our preferred partner for complex structural projects."
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>JH</span>
                  </div>
                  <div>
                    <div className="text-white font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Jason Hoven</div>
                    <div className="text-gray-400 text-sm font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Owner, Malapai Builders</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;